import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { CreditCard, Package, MapPin, Truck, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [pickupLocations, setPickupLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [guestData, setGuestData] = useState({ email: '', name: '' });
  const [whatsappNumber, setWhatsappNumber] = useState('');

  useEffect(() => {
    fetchCart();
    fetchPickupLocations();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        withCredentials: true
      });
      if (response.data.items.length === 0) {
        navigate('/cart');
        return;
      }
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Error al cargar el carrito');
    } finally {
      setLoading(false);
    }
  };

  const fetchPickupLocations = async () => {
    try {
      const response = await axios.get(`${API}/pickup-locations`);
      setPickupLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching pickup locations:', error);
    }
  };

  const calculateTotal = () => {
    return cart.items.reduce((sum, item) => {
      const finalPrice = item.product.discount_percentage > 0 
        ? item.product.price * (1 - item.product.discount_percentage / 100)
        : item.product.price;
      return sum + (finalPrice * item.quantity);
    }, 0);
  };

  const handleCheckout = async () => {
    // Validate
    if (deliveryType === 'pickup' && !selectedLocation) {
      toast.error('Por favor selecciona un punto de retiro');
      return;
    }
    
    if (deliveryType === 'delivery' && (!deliveryAddress || !deliveryCity)) {
      toast.error('Por favor completa la dirección de entrega');
      return;
    }

    if (!user && (!guestData.email || !guestData.name)) {
      toast.error('Por favor completa tu información');
      return;
    }

    setProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const payload = {
        delivery_type: deliveryType,
        pickup_location_id: deliveryType === 'pickup' ? selectedLocation : null,
        delivery_address: deliveryType === 'delivery' ? deliveryAddress : null,
        delivery_city: deliveryType === 'delivery' ? deliveryCity : null,
        ...(user ? {} : { guest_email: guestData.email, guest_name: guestData.name })
      };
      
      const response = await axios.post(`${API}/orders/checkout`, payload);
      
      if (user) {
        toast.success('¡Pago completado exitosamente! Revisa tu email.');
        navigate('/orders');
      } else {
        toast.success(`¡Pago completado! Confirmación enviada a ${guestData.email}`);
        navigate('/shop');
      }
    } catch (error) {
      console.error('Error processing checkout:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessing(false);
    }
  };

  const handleWhatsAppCheckout = async () => {
    // Validate WhatsApp number
    if (!whatsappNumber || whatsappNumber.length < 8) {
      toast.error('Por favor ingresa un número de WhatsApp válido');
      return;
    }

    if (!user && (!guestData.email || !guestData.name)) {
      toast.error('Por favor completa tu información');
      return;
    }

    try {
      setProcessing(true);

      // Prepare lead data
      const leadItems = cart.items.map(item => ({
        product_id: item.product.id,
        product_name: item.product.name,
        product_category: item.product.category,
        quantity: item.quantity,
        price: item.product.price,
        discount_percentage: item.product.discount_percentage || 0
      }));

      // Save lead to database
      await axios.post(`${API}/whatsapp-leads`, {
        customer_name: user ? user.name : guestData.name,
        customer_email: user ? user.email : guestData.email,
        customer_whatsapp: whatsappNumber,
        items: leadItems
      });

      // Generate WhatsApp message
      const total = calculateTotal();
      let message = `¡Hola! Me interesa realizar un pedido:\n\n`;
      message += `👤 *${user ? user.name : guestData.name}*\n`;
      message += `📧 ${user ? user.email : guestData.email}\n\n`;
      message += `🛍️ *Productos:*\n`;
      
      cart.items.forEach(item => {
        const finalPrice = item.product.discount_percentage > 0 
          ? item.product.price * (1 - item.product.discount_percentage / 100)
          : item.product.price;
        message += `• ${item.quantity}x ${item.product.name} - S/ ${finalPrice.toFixed(2)}\n`;
      });
      
      message += `\n💰 *Total: S/ ${total.toFixed(2)}*`;

      // Open WhatsApp
      const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      toast.success('Pedido guardado. ¡Abriendo WhatsApp!');
      
      // Optional: Clear cart after successful WhatsApp lead creation
      setTimeout(() => {
        navigate('/shop');
      }, 2000);

    } catch (error) {
      console.error('Error creating WhatsApp lead:', error);
      toast.error('Error al procesar la solicitud por WhatsApp');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-purple-400 text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="checkout-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-testid="checkout-title">
          Finalizar Compra
        </h1>

        <div className="space-y-8">
          {/* Guest Information */}
          {!user && (
            <div className="glass rounded-xl p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Información de Contacto</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="guest-name">Nombre Completo</Label>
                  <Input
                    id="guest-name"
                    type="text"
                    value={guestData.name}
                    onChange={(e) => setGuestData({ ...guestData, name: e.target.value })}
                    required
                    className="bg-gray-900/50 border-purple-900/50 text-white"
                    placeholder="Tu nombre"
                    data-testid="guest-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="guest-email">Email</Label>
                  <Input
                    id="guest-email"
                    type="email"
                    value={guestData.email}
                    onChange={(e) => setGuestData({ ...guestData, email: e.target.value })}
                    required
                    className="bg-gray-900/50 border-purple-900/50 text-white"
                    placeholder="tu@email.com"
                    data-testid="guest-email-input"
                  />
                  <p className="text-gray-400 text-sm mt-2">
                    Recibirás la confirmación de tu orden en este email
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Type Selection */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Truck className="mr-2" /> Método de Entrega
            </h2>
            
            <RadioGroup value={deliveryType} onValueChange={setDeliveryType}>
              <div className="flex items-center space-x-2 mb-4 p-4 rounded-lg border border-purple-900/50 hover:border-purple-600 cursor-pointer">
                <RadioGroupItem value="pickup" id="pickup" data-testid="pickup-radio" />
                <Label htmlFor="pickup" className="flex-1 cursor-pointer">
                  <div className="flex items-center">
                    <MapPin className="mr-2" />
                    <div>
                      <div className="font-bold">Retiro en Tienda</div>
                      <div className="text-sm text-gray-400">Retira tu pedido en uno de nuestros puntos</div>
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 p-4 rounded-lg border border-purple-900/50 hover:border-purple-600 cursor-pointer">
                <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-radio" />
                <Label htmlFor="delivery" className="flex-1 cursor-pointer">
                  <div className="flex items-center">
                    <Truck className="mr-2" />
                    <div>
                      <div className="font-bold">Envío a Domicilio</div>
                      <div className="text-sm text-gray-400">Recibe tu pedido en la dirección que indiques</div>
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>

            {deliveryType === 'pickup' && (
              <div className="mt-6">
                <Label htmlFor="location">Selecciona Punto de Retiro</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="bg-gray-900/50 border-purple-900/50 text-white" data-testid="pickup-location-select">
                    <SelectValue placeholder="Selecciona una ubicación" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                    {pickupLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        <div>
                          <div className="font-bold">{location.name}</div>
                          <div className="text-sm text-gray-400">{location.address}, {location.city}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {deliveryType === 'delivery' && (
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="address">Dirección de Entrega</Label>
                  <Input
                    id="address"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    className="bg-gray-900/50 border-purple-900/50 text-white"
                    placeholder="Calle, número, departamento"
                    data-testid="delivery-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    value={deliveryCity}
                    onChange={(e) => setDeliveryCity(e.target.value)}
                    className="bg-gray-900/50 border-purple-900/50 text-white"
                    placeholder="Tu ciudad"
                    data-testid="delivery-city-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center" data-testid="order-items-title">
              <Package className="mr-2" /> Productos en tu Orden
            </h2>
            
            <div className="space-y-4">
              {cart.items.map((item) => {
                const finalPrice = item.product.discount_percentage > 0 
                  ? item.product.price * (1 - item.product.discount_percentage / 100)
                  : item.product.price;
                
                return (
                  <div key={item.product.id} className="flex items-center justify-between border-b border-purple-900/30 pb-4" data-testid="checkout-item">
                    <div className="flex items-center gap-4">
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                        data-testid="checkout-item-image"
                      />
                      <div>
                        <h3 className="text-white font-bold" data-testid="checkout-item-name">{item.product.name}</h3>
                        <p className="text-gray-400 text-sm" data-testid="checkout-item-quantity">Cantidad: {item.quantity}</p>
                      </div>
                    </div>
                    <div className="text-purple-400 font-bold" data-testid="checkout-item-total">
                      {formatCurrency(finalPrice * item.quantity)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Simulation */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center" data-testid="payment-title">
              <CreditCard className="mr-2" /> Pago (Simulado)
            </h2>
            
            <div className="bg-purple-600/10 border border-purple-600/30 rounded-lg p-6 mb-6">
              <p className="text-gray-300 mb-4">
                🔒 Este es un pago simulado. No se procesarán cargos reales.
              </p>
              <p className="text-gray-400 text-sm">
                Al hacer clic en "Confirmar Pago", se creará tu orden automáticamente.
              </p>
            </div>

            <div className="border-t border-purple-900/50 pt-6 space-y-4">
              <div className="flex justify-between text-gray-300">
                <span>Subtotal</span>
                <span data-testid="checkout-subtotal">{formatCurrency(calculateTotal())}</span>
              </div>
              <div className="flex justify-between text-gray-300">
                <span>Envío</span>
                <span className="text-green-400">{deliveryType === 'pickup' ? 'GRATIS' : 'S/ 0.00'}</span>
              </div>
              <div className="flex justify-between text-2xl font-bold text-white">
                <span>Total a Pagar</span>
                <span data-testid="checkout-total">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          {/* WhatsApp Option */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageCircle size={24} className="text-green-400" />
              <h2 className="text-xl font-bold text-white">¿Prefieres coordinar por WhatsApp?</h2>
            </div>
            <p className="text-gray-400 mb-4 text-sm">
              Envíanos tu pedido por WhatsApp y nuestro equipo te contactará para confirmar detalles, precios y forma de pago.
            </p>
            <div className="mb-4">
              <Label htmlFor="whatsapp">Número de WhatsApp</Label>
              <Input
                id="whatsapp"
                type="tel"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+1234567890"
                className="bg-gray-900/50 border-purple-900/50 text-white"
              />
            </div>
            <Button
              onClick={handleWhatsAppCheckout}
              disabled={processing}
              className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-4 rounded-full flex items-center justify-center gap-2"
            >
              <MessageCircle size={20} />
              {processing ? 'Procesando...' : 'Enviar por WhatsApp'}
            </Button>
          </div>

          {/* Confirm Button */}
          <div className="glass rounded-xl p-4">
            <p className="text-center text-gray-400 text-sm mb-4">O finaliza tu compra directamente:</p>
            <Button
              onClick={handleCheckout}
              disabled={processing}
              className="w-full btn-primary bg-purple-600 hover:bg-purple-700 text-white text-lg py-6 rounded-full"
              data-testid="confirm-payment-button"
            >
              {processing ? 'Procesando...' : 'Confirmar Pago'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;